// Copyright (c) Docugami, Inc. All rights reserved.

import { ITextField, IStyle, TextField, Stack } from "@fluentui/react";
import { CardTextContent } from "components/cards/CardTextContent";
import { ICommandButton } from "components/cards/CommandButton";
import { ItemSlot } from "components/cards/ItemSlot";
import { DgBlueColoredButtonProps, DgColoredButton } from "components/common/DgCommonStyledComponents";
import { dgTagFontFamily, userFontFamily } from "components/common/Fonts";
import { globalEngine } from "components/doc_hosting/doc_hosts/web/presentation/WebDocHost";
import { ExplorerTextButton } from "components/myContentExplorer/ExplorerStyles";
import { primaryTheme, emptyGray, themeBlue, themeRedDark, homeGray } from "components/shell/presentation/AppStyles";
import { useMultiSelectChunks } from "hooks/MultiSelectChunks";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { TokenRange } from "networking/api";
import { useRef, useState, useEffect, useMemo } from "react";
import { createFocusViewEntity } from "sdv/engine/EntityFactory";
import { selectTokens } from "store/dgml/dgmlSelectors";
import { setSelectionMode, setSelectionModeLocked } from "store/documents/actions";
import { CanvasSelectionMode } from "store/documents/types";
import { selectClusterId, selectReportDocumentId } from "store/reports/selectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { TokensTextParser } from "utils/TokensTextParser";

const Root = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
`

const CommandBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: flex-end;
    align-items: baseline;
    width: 100%;
`

const SaveButton = styled(DgColoredButton)`
    font-size: 12px;
    padding: 4px 7px;
`

const CancelButton = styled(ExplorerTextButton)`
    font-size: 12px;
    text-align: bottom;
    >span {
        justify-self: flex-end;
    }
`

const CapsuleListContainer = styled.div`
    margin-top: 5px;
    max-height: 115px;
    overflow-y: auto;
    overflow-x: hidden;
`;

const TipText = styled.span`
    font-size: 11px;
    color: ${homeGray};
    font-style: italic;
`;

export interface PropertyCreatorProps {
    onSave: (label: string, value: string, tokenRanges?: TokenRange[]) => void;
    validate: (label: string, value: string) => boolean;
    onCancel: () => void;
    isSaving?: boolean;
}

export const PropertyCreator = (props: PropertyCreatorProps): JSX.Element => {
    const { onCancel } = props;
    const keyTextFieldRef = useRef<ITextField>();
    const valueTextFieldRef = useRef<ITextField>();

    const documentId = useAppSelector(selectReportDocumentId);
    const docsetId = useAppSelector(selectClusterId);
    const fileId = useAppSelector(state => state.reportsSlice.fileId ?? '');
    const tokens = useAppSelector(state => selectTokens(state, docsetId, fileId))
    const selection = useAppSelector(selectSelectedChunkInstance);
    const currentMode = useAppSelector(state => state.documents.selectionMode);

    const { startMultiSelect, spanIds, spanToTokenRangeLookup, removeMultiSelectChunk, stopMultiSelect } = useMultiSelectChunks(documentId, globalEngine);

    const [label, setLabel] = useState<string>('');
    const [value, setValue] = useState<string>('');

    const headerTextStyle: IStyle = {
        fontFamily: dgTagFontFamily,
        fontWeight: 700,
        fontSize: "16px",
        color: primaryTheme.palette.black,
        borderColor: themeBlue
    }

    const valueTextStyle: IStyle = {
        fontFamily: userFontFamily,
        fontWeight: 500,
        fontSize: "12px",
        color: primaryTheme.palette.black,
        borderColor: themeBlue
    }

    const dispatch = useAppDispatch();
    useEffect(() => {
        const originalMode = currentMode;

        startMultiSelect([]);
        dispatch(setSelectionMode(CanvasSelectionMode.Highlight));
        dispatch(setSelectionModeLocked(true));

        return () => {
            dispatch(setSelectionMode(originalMode));
            dispatch(setSelectionModeLocked(false));
            stopMultiSelect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!!selection) {
            props.onCancel()
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selection]);

    const capsuleProps: TextCapsuleProps[] = useMemo(() => spanIds.map(spanId => ({
        value: spanToTokenRangeLookup[spanId] ? new TokensTextParser(tokens ?? []).getTextForTokenRange(spanToTokenRangeLookup[spanId], true).join(' ') : '',
        spanId: spanId,
        onClick: () => {
            globalEngine?.queuePreUpdateCommand((entityManager, resources) => {
                if (resources.set("selectedEntityName", spanId ?? '')) {
                    createFocusViewEntity(entityManager, spanToTokenRangeLookup[spanId], true);
                }
            });
        },
        onRemove: () => removeMultiSelectChunk(spanId)
    })), [spanIds, spanToTokenRangeLookup, tokens, removeMultiSelectChunk]);

    useEffect(() => {
        if (!!capsuleProps.length) {
            setValue(capsuleProps.map(props => props.value).join(' ').trim());
        }
    }, [capsuleProps]);

    return (
        <Root>
            <Stack tokens={{ childrenGap: 10, maxWidth: '100%' }}>
                <TextField
                    label='Give your property a label:'
                    componentRef={keyTextFieldRef as React.MutableRefObject<ITextField>}
                    autoFocus={true}
                    styles={{
                        field: {
                            ...headerTextStyle,
                            "::placeholder": {
                                ...headerTextStyle,
                                color: emptyGray
                            }
                        },
                        fieldGroup: {
                            "::after": {
                                borderColor: themeBlue
                            }
                        },
                        wrapper: {
                            "::after": {
                                borderColor: themeBlue
                            }
                        }
                    }}
                    value={label}
                    placeholder={"Enter a label..."}
                    onKeyPress={ev => {
                        if (!!keyTextFieldRef.current?.value && ev.key === "Enter") {
                            valueTextFieldRef.current?.focus();
                        }
                    }}
                    onChange={(ev, value) => setLabel(value ?? '')} />
                <div>
                    <TextField
                        label="Add your content:"
                        componentRef={valueTextFieldRef as React.MutableRefObject<ITextField>}
                        autoFocus={false}
                        disabled={!!spanIds.length}
                        readOnly={!!spanIds.length}
                        styles={{
                            field: valueTextStyle,
                            fieldGroup: {
                                "::after": {
                                    borderColor: themeBlue
                                }
                            }
                        }}
                        multiline
                        resizable={false}
                        rows={3}
                        value={value}
                        placeholder={"Enter a value..."}
                        onChange={(ev, value) => setValue(value ?? '')} />
                    <CapsuleListContainer>
                        <Stack horizontal wrap>
                            {
                                capsuleProps.map(props => (
                                    <TextCapsule key={props.spanId} {...props} />
                                ))
                            }
                        </Stack>
                    </CapsuleListContainer>
                    {
                        !spanIds.length && (
                            <TipText>Tip: You can select text from the document to populate this field</TipText>
                        )
                    }
                </div>
                <CommandBarContainer>
                    <CancelButton onClick={onCancel}>Cancel</CancelButton>
                    <SaveButton
                        controlButtonProps={DgBlueColoredButtonProps}
                        onClick={() => {
                            let tokenRanges: TokenRange[] | undefined;
                            if (!!spanIds.length) {
                                tokenRanges = spanIds.map(spanId => spanToTokenRangeLookup[spanId]);
                            }

                            props.onSave(label, value, tokenRanges)
                        }} disabled={!props.validate(label, value)}>Save</SaveButton>
                </CommandBarContainer>
            </Stack>
        </Root>
    )
}

const CapsuleContainer = styled.div`
    max-width: 150px;
    cursor: pointer;
`

interface TextCapsuleProps {
    value: string;
    spanId: string;
    onClick?: () => void;
    onRemove?: () => void
}

const TextCapsule = (props: TextCapsuleProps): JSX.Element => {
    const commands: ICommandButton[] = [
        {
            icon: {
                name: 'Delete',
                width: 1,
                height: 1,
                defaultColor: themeRedDark
            },
            label: 'Remove',
            showLabel: false,
            isOverflow: false,
            canExecute: true,
            onClick: props.onRemove
        }
    ];

    return (
        <CapsuleContainer>
            <ItemSlot
                id={props.spanId}
                isConfirmed={true}
                showCommandsList={true}
                commands={commands}
                onClick={props.onClick}>
                <CardTextContent text={props.value} maxLines={1} />
            </ItemSlot>
        </CapsuleContainer>
    );
}
